import { useState } from 'react'
import { qrPngUrl, tableCardPdfUrl, updateEvent } from '../api/owner'
import { useEvent } from './eventContext'
import { EventScreen } from './components/EventScreen'
import { QrCode } from './components/QrCode'
import { readable } from './format'

const defaultCaption = 'Scan mij voor de foto’s'

/**
 * Ronde 20C en 20S — de QR-code.
 *
 * De code zelf komt van de API, niet uit een bibliotheek in de browser: zo is
 * wat je op het scherm ziet gegarandeerd dezelfde code als op het gedrukte
 * kaartje, want er is maar één plek die hem tekent.
 */
export function EventQrPage() {
  const { subject, refresh } = useEvent()
  const [caption, setCaption] = useState(subject.qrCaption ?? defaultCaption)
  const [copied, setCopied] = useState(false)

  async function saveCaption() {
    if (caption.trim() === (subject.qrCaption ?? defaultCaption)) {
      return
    }

    await updateEvent(subject.id, { qrCaption: caption.trim() })
    await refresh()
  }

  async function share() {
    // De link mét token: dat is wat een gast nodig heeft.
    await navigator.clipboard.writeText(subject.guestUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2500)
  }

  return (
    <EventScreen
      title="QR-code"
      eventTitle={subject.title}
      desktopSubtitle="Leg deze op de tafels. Gasten scannen en uploaden, zonder account."
    >
      <div className="lg:flex lg:items-start lg:gap-10">
        {/* --- Het kaartje ------------------------------------------- */}
        <div className="rounded-dialog border-line-card bg-surface border px-6 pt-6.5 pb-6 text-center shadow-[0_8px_20px_rgba(42,35,32,0.07)] lg:w-95 lg:shrink-0 lg:px-7.5 lg:pt-8 lg:pb-7.5">
          <p className="font-display text-[26px] leading-[1.1] lg:text-[30px]">
            {subject.coupleNames}
          </p>
          <p className="text-ink-faint mt-2 text-xs tracking-[0.2em] uppercase lg:mt-2.5">
            {caption}
          </p>

          <div className="mt-5.5 flex justify-center lg:mt-6.5">
            <QrCode src={qrPngUrl(subject.id)} size={190} className="lg:hidden" />
            <QrCode src={qrPngUrl(subject.id)} size={240} className="hidden lg:block" />
          </div>

          <p className="text-ink-muted mt-5 font-mono text-[13px] lg:mt-5.5 lg:text-sm">
            {readable(subject.displayUrl)}
          </p>
        </div>

        {/* --- Downloaden -------------------------------------------- */}
        <div className="mt-4.5 lg:mt-0 lg:min-w-0 lg:flex-1">
          <p className="text-ink-faint mb-3 hidden text-xs tracking-[0.18em] uppercase lg:block">
            Downloaden
          </p>

          <div className="flex flex-col gap-2.5 lg:max-w-130">
            <DownloadRow
              href={qrPngUrl(subject.id)}
              download
              icon="⤓"
              title="Downloaden als PNG"
              desktopTitle="PNG"
              detail="Los, voor eigen ontwerp"
            />
            <DownloadRow
              href={tableCardPdfUrl(subject.id)}
              download
              icon="▭"
              title="Tafelkaartje als PDF"
              detail="A6, met deze tekst erop"
            />
            <DownloadRow
              onClick={() => void share()}
              icon="↗"
              title={copied ? 'Link gekopieerd' : 'Link delen'}
              detail="Voor wie niet kan scannen"
            />
          </div>

          <p className="text-ink-faint mt-6 mb-3 text-[11px] tracking-[0.18em] uppercase lg:mt-8 lg:text-xs">
            Tekst op het kaartje
          </p>
          <input
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            onBlur={() => void saveCaption()}
            maxLength={80}
            className="font-display rounded-notice border-line-card bg-surface text-ink-muted focus:border-accent w-full border px-4 py-3.75 text-lg outline-none lg:max-w-130 lg:px-4.5 lg:py-4 lg:text-xl"
          />

          <p className="text-ink-soft mt-3 text-[13px] leading-[1.5] lg:max-w-130 lg:text-sm">
            Gasten hoeven niets te installeren. De code blijft na het feest werken, zolang het album
            online staat.
          </p>
        </div>
      </div>
    </EventScreen>
  )
}

function DownloadRow({
  href,
  download,
  onClick,
  icon,
  title,
  desktopTitle,
  detail,
}: {
  href?: string
  download?: boolean
  onClick?: () => void
  icon: string
  title: string
  desktopTitle?: string
  detail: string
}) {
  const inside = (
    <>
      <span className="bg-tint text-accent flex size-10 shrink-0 items-center justify-center rounded-[11px] text-[17px] lg:size-11">
        {icon}
      </span>
      <span className="flex-1 text-left">
        <span className="block text-[15px] font-semibold lg:text-base">
          <span className={desktopTitle ? 'lg:hidden' : ''}>{title}</span>
          {desktopTitle && <span className="hidden lg:inline">{desktopTitle}</span>}
        </span>
        <span className="text-ink-soft mt-0.5 block text-[13px] lg:text-sm">{detail}</span>
      </span>
      <span aria-hidden="true" className="text-ink-ghost text-lg">
        ›
      </span>
    </>
  )

  const shell =
    'flex items-center gap-3.25 rounded-notice border border-line-card bg-surface p-3.5 px-3.75 ' +
    'transition-colors hover:border-line-warm hover:bg-notice lg:gap-3.5 lg:p-4 lg:px-4.5'

  return href ? (
    <a href={href} download={download} className={shell}>
      {inside}
    </a>
  ) : (
    <button type="button" onClick={onClick} className={shell}>
      {inside}
    </button>
  )
}
